import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { HeaderViewer } from '@/lib/auth/viewer'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { ChevronDown, LogOut, Pencil, Store, Tag, UserRound } from 'lucide-react'

import { cn } from '@/lib/utils'
import { authClient } from '@/lib/auth/client'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

/**
 * The signed-in account menu in the header.
 *
 * Two surfaces, ONE set of entries and ONE sign-out, because the pair drifting
 * apart is the whole reason this lives in a single file:
 *
 *   - `AccountMenu` is the dropdown in the navigation bar. It is the only
 *     account control on desktop and it is present on a phone too, since the
 *     bar's right-hand plate is hidden below 640px and leaves the space free.
 *   - `AccountPanel` is the same account inside the mobile overlay. It is a
 *     plain list rather than a nested dropdown, because the panel sits above
 *     the popup's own stacking context and a menu opened inside it would be
 *     drawn behind the sheet.
 *
 * The identity is a PROP, resolved on the server by `headerViewer` from the
 * session the page guard already established. Nothing here reads a session, and
 * nothing here asks Telegram for anything: the avatar, the display name and the
 * handle are all already known by the time the markup is sent, so the menu is
 * in the first paint instead of appearing a moment later.
 */

interface AccountEntry {
    href: string
    label: string
    icon: LucideIcon
}

/**
 * Where this identity can go. Derived from the projection and nothing else, so
 * the bar and the panel can never offer different destinations.
 *
 * `profilePath` is resolved on the SERVER by `headerViewer`: the member's own
 * platform handle when they have chosen one, otherwise their Telegram username.
 * The two are not the same thing and the menu no longer assumes they are — a
 * Telegram username can change hands, a chosen handle is theirs. Somebody with
 * neither has no public address yet, so that entry is absent rather than a link
 * into a 404, while EDIT PROFILE is always there, since that is where they get
 * one.
 */
export const accountEntries = (viewer: HeaderViewer): Array<AccountEntry> => [
    { href: '/communities', label: 'My marketplaces', icon: Store },
    { href: '/my-listings', label: 'My listings', icon: Tag },
    ...(viewer.profilePath
        ? [{ href: viewer.profilePath, label: 'My public profile', icon: UserRound }]
        : []),
    { href: '/account/profile', label: 'Edit profile', icon: Pencil }
]

const initials = (name: string): string => name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?'

/**
 * The avatar plate. Square with the MTA corner, never the round default, so it
 * reads as part of this design system rather than a stock account chip.
 */
const ViewerFace: FC<{ viewer: HeaderViewer, className?: string }> = ({ viewer, className }): ReactNode => {
    if (!viewer.image) {
        return (
            <span
                aria-hidden='true'
                className={cn(
                    'flex shrink-0 items-center justify-center rounded-mta-xs bg-plate text-[12px] font-extrabold text-ink-900',
                    className
                )}
            >
                {initials(viewer.name)}
            </span>
        )
    }

    /*
        Telegram serves avatars from hosts we do not control and the project
        declares no remote image patterns, so a plain img keeps this working
        without opening the optimiser to arbitrary hosts. The dimensions are
        fixed, so the space is reserved and a slow avatar shifts nothing.

        Decorative: the trigger carries the name in its accessible label and the
        panel writes it out beside this, so announcing it again would repeat.
    */
    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={viewer.image}
            alt=''
            width={40}
            height={40}
            decoding='async'
            className={cn('shrink-0 rounded-mta-xs object-cover', className)}
        />
    )
}

/**
 * Sign out, through the ONE flow this app already has.
 *
 * `authClient.signOut()` is Better Auth's own endpoint: it deletes the session
 * row and expires the cookie server-side, and clears the browser's copy of the
 * session as it resolves. There is no second session to tidy up here, and this
 * hook deliberately holds no state of its own beyond the in-flight flag.
 *
 * It then lands on the public home page, which resolves its own header state on
 * the server, so what renders next is what the server allows for a signed-out
 * visitor rather than a stale copy of what it allowed a moment ago.
 */
export const useSignOut = (): { signOut: () => void, signingOut: boolean } => {
    const router = useRouter()
    const [signingOut, setSigningOut] = useState(false)

    const signOut = (): void => {
        if (signingOut) return

        setSigningOut(true)

        void (async () => {
            try {
                await authClient.signOut()
            } catch {
                // The cookie is expired server-side either way; the reload
                // below is what settles what this browser is now allowed.
            }

            try {
                await router.replace('/')
            } catch {
                // A cancelled route change leaves us here, so release the
                // control rather than stranding it mid-flight.
                setSigningOut(false)
            }
        })()
    }

    return { signOut, signingOut }
}

/* ---------------------------------------------------------------
 * The bar
 * ------------------------------------------------------------- */

interface AccountMenuProps {
    viewer: HeaderViewer
    className?: string
}

export const AccountMenu: FC<AccountMenuProps> = ({ viewer, className }): ReactNode => {
    const { signOut, signingOut } = useSignOut()

    const handle = viewer.username ? `@${viewer.username}` : null

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={(
                    <button
                        type='button'
                        /* Contains the visible name, so the spoken label and the
                           written one agree for voice control. */
                        aria-label={`Account menu for ${viewer.name}`}
                        className={cn(
                            'mta-focus inline-flex min-h-11 items-center gap-2.5 rounded-mta-sm border-2 border-ink-900 bg-paper px-2 py-1.5',
                            'transition-colors duration-150 hover:bg-plate data-open:bg-plate lg:pr-3',
                            className
                        )}
                    />
                )}
            >
                <ViewerFace viewer={viewer} className='size-8' />

                <span className='hidden min-w-0 flex-col items-start leading-tight lg:flex'>
                    <span className='max-w-[10rem] truncate text-[13px] font-bold text-ink-900'>
                        {viewer.name}
                    </span>

                    {handle && (
                        <span className='max-w-[10rem] truncate text-[11.5px] text-ink-900/60'>
                            {handle}
                        </span>
                    )}
                </span>

                <ChevronDown className='hidden size-4 shrink-0 text-ink-900/60 lg:block' aria-hidden='true' />
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align='end'
                sideOffset={10}
                className='w-[17rem] rounded-mta-md border-2 border-ink-900 bg-paper p-1.5 shadow-offset-sm ring-0'
            >
                {/* Display only, and not focusable, so tabbing lands on the
                    first real destination rather than on a heading. */}
                <div className='flex items-center gap-3 px-2 py-2.5'>
                    <ViewerFace viewer={viewer} className='size-10' />

                    <span className='min-w-0 flex-1'>
                        <span className='block truncate text-[14px] font-bold text-ink-900'>
                            {viewer.name}
                        </span>

                        {handle && (
                            <span className='block truncate text-[12.5px] text-ink-900/60'>
                                {handle}
                            </span>
                        )}
                    </span>
                </div>

                <p className='flex items-center gap-1.5 px-2 pb-2 text-[11.5px] text-ink-900/55'>
                    <TelegramGlyph className='size-3.5 shrink-0 text-[var(--verify-telegram)]' />
                    Telegram account verified
                </p>

                <DropdownMenuSeparator className='bg-ink-900/15' />

                {accountEntries(viewer).map((entry) => (
                    <DropdownMenuItem
                        key={entry.href}
                        render={<Link href={entry.href} />}
                        className='min-h-11 gap-2.5 rounded-mta-sm px-2 text-[14px] font-semibold text-ink-900 focus:bg-plate'
                    >
                        <entry.icon className='size-4 shrink-0 text-ink-900/60' aria-hidden='true' />
                        {entry.label}
                    </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator className='bg-ink-900/15' />

                <DropdownMenuItem
                    variant='destructive'
                    disabled={signingOut}
                    onClick={signOut}
                    className='min-h-11 gap-2.5 rounded-mta-sm px-2 text-[14px] font-semibold'
                >
                    <LogOut className='size-4 shrink-0' aria-hidden='true' />
                    {signingOut ? 'Signing out…' : 'Log out'}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

/* ---------------------------------------------------------------
 * The mobile panel
 * ------------------------------------------------------------- */

interface AccountPanelProps {
    viewer: HeaderViewer
    /** Closes the sheet, so a tap never leaves it open over the new page. */
    onNavigate: () => void
}

/**
 * The same account, written out inside the mobile overlay.
 *
 * A list rather than a dropdown for a stacking reason, not a design one: the
 * sheet renders above the popup layer, so a menu opened from inside it would be
 * hidden behind the sheet itself.
 */
export const AccountPanel: FC<AccountPanelProps> = ({ viewer, onNavigate }): ReactNode => {
    const { signOut, signingOut } = useSignOut()

    const handle = viewer.username ? `@${viewer.username}` : null

    return (
        <div className='mt-3 border-t-2 border-ink-900/15 pt-3'>
            <div className='flex items-center gap-3 px-3 py-2'>
                <ViewerFace viewer={viewer} className='size-10' />

                <div className='min-w-0 flex-1'>
                    <p className='truncate text-[14px] font-bold text-ink-900'>
                        {viewer.name}
                    </p>

                    <p className='flex items-center gap-1.5 truncate text-[12px] text-ink-900/60'>
                        <TelegramGlyph className='size-3.5 shrink-0 text-[var(--verify-telegram)]' />
                        {handle ?? 'Telegram account verified'}
                    </p>
                </div>
            </div>

            <ul className='grid gap-1'>
                {accountEntries(viewer).map((entry) => (
                    <li key={entry.href}>
                        <Link
                            href={entry.href}
                            onClick={onNavigate}
                            className='mta-focus flex min-h-11 items-center gap-2.5 rounded-mta-sm px-3 py-3 text-[15px] font-semibold text-ink-900 transition-colors hover:bg-surface-muted'
                        >
                            <entry.icon className='size-4 shrink-0 text-ink-900/60' aria-hidden='true' />
                            {entry.label}
                        </Link>
                    </li>
                ))}

                <li>
                    <button
                        type='button'
                        disabled={signingOut}
                        onClick={signOut}
                        className='mta-focus flex min-h-11 w-full items-center gap-2.5 rounded-mta-sm px-3 py-3 text-left text-[15px] font-semibold text-brand-red-ink transition-colors hover:bg-brand-red/10 disabled:opacity-60'
                    >
                        <LogOut className='size-4 shrink-0' aria-hidden='true' />
                        {signingOut ? 'Signing out…' : 'Log out'}
                    </button>
                </li>
            </ul>
        </div>
    )
}
